import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { exchangeSession } from "../lib/auth";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";

/**
 * AuthReturn — landing page after Emergent OAuth redirect.
 * Reads `session_id` from URL fragment, exchanges it via the backend,
 * then redirects to the `to` path passed as query param.
 */
const AuthReturn = () => {
    const nav = useNavigate();
    const [params] = useSearchParams();
    const [err, setErr] = useState(null);
    const done = useRef(false); // prevent StrictMode double-run

    useEffect(() => {
        if (done.current) return;
        done.current = true;

        const to = params.get("to") || "/owner";
        const hash = window.location.hash || "";
        const match = hash.match(/session_id=([^&]+)/);
        const sessionId = match ? decodeURIComponent(match[1]) : null;

        if (!sessionId) {
            setErr("Missing session token in redirect URL");
            return;
        }

        exchangeSession(sessionId)
            .then((data) => {
                // Clear the hash so a refresh doesn't re-exchange
                window.history.replaceState(null, "", window.location.pathname + window.location.search);
                nav(to, { replace: true, state: { user: data?.user, linked_tenants: data?.linked_tenants } });
            })
            .catch((e) => setErr(e?.response?.data?.detail || "Sign-in failed"));
    }, [nav, params]);

    return (
        <div className="min-h-screen bg-[#F9F6F0] flex items-center justify-center p-6 text-center">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-md">
                {!err && (
                    <>
                        <Loader2 className="h-8 w-8 text-[#FF4F18] animate-spin mx-auto" strokeWidth={2.5} />
                        <div className="mt-6 font-display font-black text-2xl">Signing you in…</div>
                        <div className="mt-2 text-sm text-[#8C7A72]">
                            Almost there. Grabbing your Coffeein7 account.
                        </div>
                    </>
                )}
                {err && (
                    <>
                        <div className="font-display font-black text-2xl text-[#FF4F18]">
                            Sign-in problem
                        </div>
                        <p className="mt-2 text-sm text-[#8C7A72]">{err}</p>
                        <button
                            data-testid="authreturn-back"
                            onClick={() => nav("/", { replace: true })}
                            className="mt-6 px-6 py-3 rounded-full bg-[#2C1A12] text-white font-display font-bold text-sm active:scale-95"
                        >
                            Back to home
                        </button>
                    </>
                )}
            </motion.div>
        </div>
    );
};

export default AuthReturn;
